import { useCallback, useState } from 'react';

import { useNavigate } from 'react-router';

import type { Notification } from '@enterprise/api';
import { isDefined } from '@enterprise/core';

import { resolveEntityRoute } from '@feat/notifications/utilities/entity-route';
import { extractRichBodyText } from '@feat/notifications/utilities/render-rich-body';

import type { UseNativeNotificationResult } from './types/use-native-notification.type';

const isNativeNotificationSupported = (): boolean => {
  return isDefined(globalThis.Notification);
};

export const useNativeNotification = (): UseNativeNotificationResult => {
  const navigate = useNavigate();
  const [, forceUpdate] = useState(0);

  const permission: NotificationPermission | 'unsupported' = isNativeNotificationSupported()
    ? globalThis.Notification.permission
    : 'unsupported';

  const requestPermission = useCallback(async (): Promise<void> => {
    if (!isNativeNotificationSupported()) {
      return;
    }

    await globalThis.Notification.requestPermission();
    // Trigger a re-render so the permission state reflects the user's choice
    forceUpdate((count) => {
      return count + 1;
    });
  }, []);

  const raise = useCallback(
    (notification: Notification): void => {
      if (!isNativeNotificationSupported()) {
        return;
      }

      if (globalThis.Notification.permission !== 'granted') {
        return;
      }

      if (document.visibilityState !== 'hidden') {
        return;
      }

      const plainBody = extractRichBodyText(notification.body);
      const nativeNotification = new globalThis.Notification(notification.title, {
        body: plainBody,
        icon: '/images/hsp_logo_180x142.png',
      });

      nativeNotification.onclick = () => {
        window.focus();

        const route = resolveEntityRoute(notification.entityType, notification.entityId);

        if (isDefined(route)) {
          void navigate(route);
        }

        nativeNotification.close();
      };
    },
    [navigate]
  );

  return { permission, raise, requestPermission };
};
